/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import './SmartLogOnInfo.scss';
import {ExdsGrid, ExdsImage, ExdsLayout, ExdsText} from '@exds/core';
import {TuxAccordion, TuxAccordionPanel, TuxLoadingIndicator} from '@tuxedo/react-component-library';
import parse from 'html-react-parser';
import ButtonGroup from '../../../ButtonGroup/ButtonGroup';
import React, {useContext, useEffect} from 'react';
import SmartLogOnHeader from '../SmartLogOnHeader/SmartLogOnHeader';
import sloAnimation from '../../../../assets/images/slo-animation.gif';
import {sanitizedHtml} from "../../../../utils/utils";
import AppContext from "../../../../AppContext";
import {STEP_SKIPPED} from "../../../../constants/constants";
import Alert from "../../../Alert/Alert";
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function SmartLogOnInfo(props) {
  const {setActiveMobileAppSetupPage, handleModalAction, skipError, setSkipError, setPageName} = props;
  const {hashStepsStatusContext, loadAA, sidContext} = useContext(AppContext);
  const hashStepsStatus = hashStepsStatusContext.at(0);
  const sid = sidContext.at(0);
  const pageName = 'Mobile App Setup | Smart Log On Info';

  const accordionContent = [
    {panelHeader:"Will I still need to remember / use my password?", panelBody:"Yes. Passwords and tokens can still be used to login to your desktop, and must be maintained for situations requiring your change/reset your password, unlock your account etc."},
    {panelHeader:"Is it mandatory to use Smart Log On?", panelBody:"No. While the use of Smart Log On is optional, it is still the easier alternative. We highly encourage you to try it" +
        " because it provides a secure and enhanced user experience. You'll still be able to use your password to log on and unlock your virtual desktop while Smart Log On is available for the foreseeable future."},
    {panelHeader:"How are my phone biometrics, data and privacy managed?", panelBody:"Smart Log On uses existing biometrics that are securely managed on your phone. Your information never leaves your phone and Smart Log On never accesses " +
        "your personal data like photos, documents,text messages, contacts, email, call/browsing history, apps, etc. If your device's biometrics are altered(FaceID or TouchID is changed, new FaceID or TouchID is added etc.) Smart Log On will stop working."},
    {panelHeader:"Can I register multiple devices for Smart Log On?", panelBody:"Due to security reasons, only one device can be registered at a time. An existing device must be unregistered before registering a new device."},
    {panelHeader:"Which permissions does the Smart Log On mobile app require?", panelBody:"You will need to grant the following permissions to properly use Smart Log On mobile app on your device: <ul>\n" +
        "                  <li><b>Camera: </b>For scanning QR code during registration and log on.</li>\n" +
        "                  <li><b>Notifications: </b>For receiving authentication requests</li>\n" +
        "                  <li><b>Face ID/Touch ID: </b>For approving biometric authentication requests</li>\n" +
        "                </ul>"}
  ];

  const modalDetails = {
    title: 'Are you sure you want to skip Smart Log On setup?',
    body: parse(sanitizedHtml('To find out more or setup Smart Log On at a later time, type <b>go/smartlogon123</b> into a browser while logged into Workspace')),
    continueButton: 'No, let\'s continue',
    modalActionButton: 'Yes, I want to skip',
  };

  useEffect(() => {
    if(loadAA) {
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinueButtonAction = () => {
    setSkipError({ showError: false, correlationId: '', isLoading: false });
    setActiveMobileAppSetupPage('downloadSmartLogOn');
  };

  return(
    <>
      {skipError?.isLoading && <div className="loading-spinner"><TuxLoadingIndicator/></div>}
      {!skipError?.isLoading &&
        <ExdsGrid columnSpan={8}  className='step-details'>
          {
            (skipError.showError && <Alert correlationId={skipError.correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/>)
          }
          <SmartLogOnHeader/>
          <ExdsText primitive="div">
            Smart Log On allows you to quickly and securely log on to the JPMC network and business applications
            using your fingerprint or face ID on a registered mobile device.
          </ExdsText>
          <ExdsText primitive="p" decoration="bold" className="scan-text-outside">
            Simply scan a QR code for access
          </ExdsText>
          <ExdsLayout
            display="flex"
            alignItems="flex-start"
            flexDirection="row"
            className='qr_instructions'
          >
            <ExdsImage src={sloAnimation} backgroundFit="contain" alt='Smart Log On Animation' className='slo-animation' />
            <ExdsText primitive="div">
              <ExdsText primitive="p" decoration="bold" className="scan-text-inside">
                Simply scan a QR code for access
              </ExdsText>
              <ExdsText primitive="p">
                Smart Log On minimises the need to enter the 16 character password and RSA token you just created,
                enhancing both security and convenience.
              </ExdsText>
            </ExdsText>
          </ExdsLayout>
          <ExdsText primitive="div" className='device-requirements'>
            <p className='section-header'>Device requirements:</p>
            <ul>
              <li>Minimum iOS 15 or Android device 6.2</li>
              <li>App camera access <span>(this allows you to scan QR codes)</span></li>
              <li>Face ID or Fingerprint registered to device <span>(allowing us authenticate you securely)</span></li>
              <li>Notification enabled <span>(for unlocking your desktop and important access related information)</span></li>
            </ul>
          </ExdsText>
          <ExdsText primitive="h3" className='faq-header'>FAQ's</ExdsText>
          <TuxAccordion className='slo-accordion-body2'>
            {
              accordionContent.map((item, index) => (
                <TuxAccordionPanel
                  key={index}
                  title={<ExdsText primitive="h3" className="accordion-title">{item.panelHeader}</ExdsText>}>
                  <p>{parse(sanitizedHtml(item.panelBody))}</p>
                </TuxAccordionPanel>
              ))
            }
          </TuxAccordion>
          <div className='device-selection'>
            <ButtonGroup
              advanceButtonContent="Continue"
              advanceButtonAction={handleContinueButtonAction}
              modalButtonContent="Skip Smart Log On Setup"
              modalButtonType="link"
              modalDetails={modalDetails}
              modalAction={() => handleModalAction('SKIP_SLO', hashStepsStatus.rsaSetup, STEP_SKIPPED, false)}
            />
          </div>
        </ExdsGrid>
      }
    </>
  );
}
